import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import RootLoading from "./components/RootLoading/RootLoading";
import Router from "./router/Router";

import { IRootState } from ".";

import Profile from "./utils/apiServices/profile";
import { login, logout } from "./features/login/reducer";
import { setLanguage } from "./features/language/reducer";
import { generateToken, zimLogin } from "./utils/chat";
import { setChat } from "./features/chat/reducer";
import { isLanguageRTL } from "./i18n/translationUtils";
import { isLanguageSupported, Language } from "./i18n/translations";
import { getUserCountryFromIP, getLanguageForCountry } from "./utils/countryValidation";

function App() {
  const events = [
    "load",
    "mousemove",
    "mousedown",
    "click",
    "scroll",
    "keypress",
  ];

  let timer: any;

  const handleLogoutTimer = () => {
    timer = setTimeout(() => {
      // clears any pending timer.
      resetTimer();
      // Listener clean up. Removes the existing event listener from the window
      Object.values(events).forEach((item) => {
        window.removeEventListener(item, resetTimer);
      });
      // logs out user
      dispatch(logout());
    }, 600000); // 10000ms = 10secs. You can change the time.
  };

  // this resets the timer if it exists.
  const resetTimer = () => {
    if (timer) clearTimeout(timer);
  };

  useEffect(() => {
    Object.values(events).forEach((item) => {
      window.addEventListener(item, () => {
        resetTimer();
        handleLogoutTimer();
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatch = useDispatch();
  const [rootLoading, setRootLoading] = useState(true);
  const user = useSelector((state: IRootState) => state.user.value);
  const { loggedIn } = user;
  const currentLanguage = useSelector((state: IRootState) => state.language.value.current);
  // access token
  const accessToken =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken") ||
    "";

  // Get Profile
  useEffect(() => {
    if (accessToken && !loggedIn) {
      Profile.getProfile()
        .then((res) => {
          if (res.success) {
            const { data } = res;

            const localToken = localStorage.getItem("accessToken");
            // Dispatch Login
            dispatch(
              login({
                id: data.id,
                first_name: data.first_name,
                last_name: data.last_name,
                phone: data.phone,
                email: data.email,
                country: data.country,
                email_verified_at: data.email_verified_at,
                password: data.password,
                bank_name: data.bank_name,
                branch: data.branch,
                holder_name: data.holder_name,
                account_no: data.account_no,
                establishment: data.establishment,
                image: data.image,
                status: data.status,
                device_token: data.device_token,
                vendor_id: data.vendor_id,
                created_at: data.created_at,
                updated_at: data.updated_at,
                phone_verified_at: data.phone_verified_at,
                account_status: data.account_status,
                temp_token: data.temp_token,
                country_id: data.country_id,
                zone_id: data.zone_id,
                state_id: data.state_id,
                loggedIn: true,
                remember_me: !!localToken,
                access_token: accessToken,
                kyc_status: data.kyc_status,
              })
            );
            const chatUserId = (
              data.establishment?.establishment_id ??
              data.establishment?.establishmentId ??
              data.vendor_id ??
              data.id
            )?.toString();
            const userName = data.establishment?.name
              ? `${data.establishment.name} (Vendor)`
              : "Vendor";
            try {
              if (!chatUserId) {
                throw new Error("Missing vendor chat user id");
              }
              const token = generateToken(chatUserId, 0);
              zimLogin({ userName, userID: chatUserId }, token)
                .then(function () {
                  dispatch(
                    setChat({
                      isLogged: true,
                      isLoading: false,
                    })
                  );
                })
                .catch((error) => {
                  console.warn(
                    "ZIM login failed:",
                    error instanceof Error ? error.message : String(error)
                  );
                  dispatch(
                    setChat({
                      isLogged: false,
                      isLoading: false,
                    })
                  );
                });
            } catch (error) {
              console.warn(
                "Token generation failed:",
                error instanceof Error ? error.message : String(error)
              );
              dispatch(
                setChat({
                  isLogged: false,
                  isLoading: false,
                })
              );
            }

            setRootLoading(false);

            if (
              !data.phone_verified_at &&
              !window.location.pathname.includes("/verify-phone")
            ) {
              window.location.assign(
                `/verify-phone?email=${encodeURIComponent(data.email)}&phone=${encodeURIComponent(data.phone || "")}`
              );
            }
          } else {
            setRootLoading(false);
            // notify("error", `${res.message}`);
          }
        })
        .catch(() => {
          setRootLoading(false);
        });
    } else {
      setRootLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  // Apply RTL/LTR direction to document based on language
  useEffect(() => {
    const isRTL = isLanguageRTL(currentLanguage);
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
    document.documentElement.setAttribute("lang", currentLanguage);
    
    // Also update body class for additional styling if needed
    if (isRTL) {
      document.body.classList.add("rtl");
      document.body.classList.remove("ltr");
    } else {
      document.body.classList.add("ltr");
      document.body.classList.remove("rtl");
    }
  }, [currentLanguage]);

  // Auto-select language from IP country when no saved preference
  useEffect(() => {
    if (localStorage.getItem("appLanguage")) return;
    getUserCountryFromIP()
      .then((isoCode) => {
        const lang = getLanguageForCountry(isoCode);
        if (lang && isLanguageSupported(lang)) {
          dispatch(setLanguage(lang as Language));
        }
      })
      .catch(() => {});
  }, [dispatch]);

  return <div className="">{rootLoading ? <RootLoading /> : <Router />}</div>;
}

export default App;
